import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { LogoWord } from "../components/Logo";
import { toast } from "sonner";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function googleLogin() {
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export default function AuthPage({ mode = "login" }) {
    const isSignup = mode === "signup";
    const navigate = useNavigate();
    const location = useLocation();
    const { loginWithEmail, signup } = useAuth();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [name, setName] = useState("");
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            if (isSignup) {
                await signup(email, password, name || email.split("@")[0]);
                toast.success("Welcome to Real-Check!");
            } else {
                await loginWithEmail(email, password);
                toast.success("Signed in");
            }
            const dest = location.state?.from || "/dashboard";
            navigate(dest);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Auth failed");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-6 py-16">
            <div className="w-full max-w-md">
                <div className="mb-8 text-center">
                    <Link to="/" className="inline-block">
                        <LogoWord />
                    </Link>
                </div>
                <div className="card-surface rounded-2xl p-8">
                    <h1 className="font-display text-3xl text-accent tracking-tight mb-1">
                        {isSignup ? "Create your account" : "Welcome back"}
                    </h1>
                    <p className="text-sm text-muted-foreground mb-6">
                        {isSignup
                            ? "Join Real-Check. Every Pro subscription helps fund Hands of Hope."
                            : "Sign in to continue fact-checking."}
                    </p>

                    <Button
                        onClick={googleLogin}
                        variant="secondary"
                        className="w-full h-11 rounded-full bg-white text-slate-900 hover:bg-white/90 border border-white/20 font-semibold"
                        data-testid="google-signin-button"
                    >
                        <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" aria-hidden>
                            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.26 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/>
                            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/>
                        </svg>
                        Continue with Google
                    </Button>

                    <div className="flex items-center gap-3 my-6">
                        <div className="flex-1 h-px bg-white/10" />
                        <span className="text-xs text-muted-foreground uppercase tracking-widest">or</span>
                        <div className="flex-1 h-px bg-white/10" />
                    </div>

                    <form onSubmit={submit} className="space-y-4">
                        {isSignup && (
                            <div>
                                <Label htmlFor="name" className="text-xs uppercase tracking-widest text-muted-foreground">
                                    Name
                                </Label>
                                <Input
                                    id="name"
                                    data-testid="auth-name-input"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    placeholder="Ada Lovelace"
                                    className="mt-1.5 bg-white/5 border-white/10 h-11"
                                    required
                                />
                            </div>
                        )}
                        <div>
                            <Label htmlFor="email" className="text-xs uppercase tracking-widest text-muted-foreground">
                                Email
                            </Label>
                            <Input
                                id="email"
                                type="email"
                                data-testid="auth-email-input"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="you@example.com"
                                className="mt-1.5 bg-white/5 border-white/10 h-11"
                                required
                            />
                        </div>
                        <div>
                            <Label htmlFor="password" className="text-xs uppercase tracking-widest text-muted-foreground">
                                Password
                            </Label>
                            <Input
                                id="password"
                                type="password"
                                data-testid="auth-password-input"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="At least 6 characters"
                                className="mt-1.5 bg-white/5 border-white/10 h-11"
                                minLength={6}
                                required
                            />
                        </div>
                        <Button
                            type="submit"
                            disabled={loading}
                            data-testid="auth-submit-button"
                            className="w-full h-11 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground font-semibold"
                        >
                            {loading ? "Please wait…" : isSignup ? "Create account" : "Sign in"}
                        </Button>
                    </form>

                    <p className="text-sm text-muted-foreground mt-6 text-center">
                        {isSignup ? (
                            <>Already have an account? <Link to="/login" className="text-primary hover:underline" data-testid="switch-to-login">Sign in</Link></>
                        ) : (
                            <>New here? <Link to="/signup" className="text-primary hover:underline" data-testid="switch-to-signup">Create an account</Link></>
                        )}
                    </p>
                </div>
            </div>
        </div>
    );
}
